import { useCallback, useMemo, useRef } from 'react';

import { trackAiBuilderTurnAnalyticsEvents } from '../utils/aiBuilderChatTracking';
import {
  type AiBuilderChatAdapter,
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderTurnRequest,
} from '../utils/types';

import { type AiBuilderMessage } from './types';
import { type useAiBuilderTurnRunner } from './useAiBuilderTurnRunner';

type RunAiBuilderEvents = ReturnType<typeof useAiBuilderTurnRunner>;
type AiBuilderTurnRunnerEventStream = Parameters<RunAiBuilderEvents>[0];
type AiBuilderUserMessageStatus = Extract<
  AiBuilderMessage,
  { kind: 'user' }
>['status'];

export type UpdateAiBuilderUserMessageStatus = (
  messageId: string,
  status: AiBuilderUserMessageStatus,
) => void;

export type CreateAiBuilderServerTurnRequest = (
  signal: AbortSignal,
) => AiBuilderTurnRequest;

type DispatchAiBuilderServerTurnOptions = {
  userMessageId?: string;
};

export const useAiBuilderTurnAbortController = () => {
  const activeTurnAbortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    const controller = activeTurnAbortControllerRef.current;
    if (!controller) return;

    controller.abort();
    activeTurnAbortControllerRef.current = null;
  }, []);

  const create = useCallback(() => {
    abort();

    const controller = new AbortController();
    activeTurnAbortControllerRef.current = controller;
    return controller;
  }, [abort]);

  const clearIfActive = useCallback((controller: AbortController) => {
    if (activeTurnAbortControllerRef.current === controller) {
      activeTurnAbortControllerRef.current = null;
    }
  }, []);

  return useMemo(
    () => ({
      abort,
      create,
      clearIfActive,
    }),
    [abort, clearIfActive, create],
  );
};

export async function* trackAiBuilderUserMessageStatusEvents(
  events: AiBuilderTurnRunnerEventStream,
  {
    activeRunIdRef,
    runId,
    signal,
    updateUserMessageStatus,
    userMessageId,
  }: {
    activeRunIdRef: { current: number };
    runId: number;
    signal?: AbortSignal;
    updateUserMessageStatus: UpdateAiBuilderUserMessageStatus;
    userMessageId: string;
  },
): AsyncIterable<AiBuilderChatOrchestrationEvent> {
  let accepted = false;
  let failed = false;

  const updateStatus = (status: AiBuilderUserMessageStatus) => {
    if (activeRunIdRef.current !== runId) return;
    updateUserMessageStatus(userMessageId, status);
  };

  try {
    for await (const event of events) {
      if (isAiBuilderTurnFailureEvent(event)) {
        if (!accepted) {
          failed = true;
          updateStatus('failed');
        }
        yield event;
        continue;
      }

      if (!accepted && isAiBuilderTurnAcceptedEvent(event)) {
        accepted = true;
        updateStatus('sent');
      }

      yield event;
    }

    if (!accepted && !failed && !signal?.aborted) {
      updateStatus('sent');
    }
  } catch (error) {
    if (!signal?.aborted) {
      updateStatus('failed');
    }

    throw error;
  }
}

const isAiBuilderTurnAcceptedEvent = (
  event: AiBuilderChatOrchestrationEvent,
) => {
  if (isAiBuilderTurnFailureEvent(event)) return false;

  return (
    event.type !== 'delay' &&
    event.type !== 'setSessionId' &&
    event.type !== 'setUsage'
  );
};

const isAiBuilderTurnFailureEvent = (
  event: AiBuilderChatOrchestrationEvent,
) => {
  // authExpired 는 오류 말풍선을 만들지 않으므로(재로그인 모달이 안내 담당),
  // append 만 보던 판정으로는 직전 유저 메시지가 '전송 실패'로 바뀌지 않는다.
  // 실패 표시는 유지하면서 문구만 감추기 위해 이 타입도 실패로 인정한다.
  if (event.type === 'authExpired') return true;

  return (
    event.type === 'append' &&
    event.message.kind === 'system' &&
    event.message.tone === 'error'
  );
};

export const useRunAiBuilderEventsSafely = ({
  activeRunIdRef,
  appendRunRejectionMessage,
  isAuthExpiredError,
  onAuthExpired,
  runAiBuilderEvents,
  turnAbortController,
  updateUserMessageStatus,
}: {
  activeRunIdRef: { current: number };
  appendRunRejectionMessage: (runId: number) => void;
  isAuthExpiredError: (error: unknown) => boolean;
  onAuthExpired: () => void;
  runAiBuilderEvents: RunAiBuilderEvents;
  turnAbortController: ReturnType<typeof useAiBuilderTurnAbortController>;
  updateUserMessageStatus: UpdateAiBuilderUserMessageStatus;
}) => {
  return useCallback(
    (
      events: Parameters<RunAiBuilderEvents>[0],
      controller?: AbortController,
      userMessageId?: string,
    ) => {
      const runId = activeRunIdRef.current + 1;
      const trackedEvents = userMessageId
        ? trackAiBuilderUserMessageStatusEvents(events, {
            activeRunIdRef,
            runId,
            updateUserMessageStatus,
            userMessageId,
            ...(controller?.signal ? { signal: controller.signal } : {}),
          })
        : events;

      void runAiBuilderEvents(trackedEvents)
        .catch((error: unknown) => {
          if (controller?.signal.aborted) return;

          // JSON 경로(세션 생성 등)의 401 은 SSE error 이벤트가 아니라 예외로 온다.
          // 일반 오류 말풍선 대신 재로그인 모달로 보낸다.
          if (isAuthExpiredError(error)) {
            onAuthExpired();
            return;
          }

          appendRunRejectionMessage(runId);
        })
        .finally(() => {
          if (controller) {
            turnAbortController.clearIfActive(controller);
          }
        });
    },
    [
      activeRunIdRef,
      appendRunRejectionMessage,
      isAuthExpiredError,
      onAuthExpired,
      runAiBuilderEvents,
      turnAbortController,
      updateUserMessageStatus,
    ],
  );
};

export const useDispatchServerTurn = ({
  runAiBuilderEventsSafely,
  turnAbortController,
}: {
  runAiBuilderEventsSafely: ReturnType<typeof useRunAiBuilderEventsSafely>;
  turnAbortController: ReturnType<typeof useAiBuilderTurnAbortController>;
}) => {
  return useCallback(
    (
      startTurn: AiBuilderChatAdapter['startTurn'],
      createRequest: CreateAiBuilderServerTurnRequest,
      options?: DispatchAiBuilderServerTurnOptions,
    ) => {
      const controller = turnAbortController.create();
      const request = createRequest(controller.signal);

      runAiBuilderEventsSafely(
        trackAiBuilderTurnAnalyticsEvents(startTurn(request), request),
        controller,
        options?.userMessageId,
      );
    },
    [runAiBuilderEventsSafely, turnAbortController],
  );
};
